import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft } from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useLocation, useNavigate } from "react-router-dom";

const BASE_URL = "https://nissi-india.com/api";

const SupervisorEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdLS,
      "x-branch": branchTypeLS,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdLS, branchTypeLS, userId]
  );

  const passedSupervisor = location.state?.supervisor || null;
  const mode = location.state?.mode || (passedSupervisor ? "edit" : "new");

  const [branches, setBranches] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [loading, setLoading] = useState(false);

  // ✅ Prevent dropdown effects from clearing values during initial edit load
  const initializingRef = useRef(false);

  const [formData, setFormData] = useState({
    supervisorid: "",
    code: "",
    name: "",

    branchId: branchIdLS || "",
    countryId: "",
    stateId: "",
    districtId: "",

    pinNo: "",
    address1: "",
    address2: "",
    phno: "",

    accNo: "",
    ifscCode: "",
    bankName: "",
    branchNameBank: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const loadBranches = async () => {
    const res = await axios.get(`${BASE_URL}/supervisors/branches`, {
      headers: commonHeaders,
    });
    const list = res.data?.data || res.data || [];
    setBranches(list);
    return list;
  };

  const loadCountries = async () => {
    const res = await axios.get(`${BASE_URL}/supervisors/countries`, {
      headers: commonHeaders,
    });
    const list = res.data?.data || res.data || [];
    setCountries(list);
    return list;
  };

  const loadStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return [];
    }

    const res = await axios.get(`${BASE_URL}/supervisors/states`, {
      params: { countryid: countryId },
      headers: commonHeaders,
    });

    const list = res.data?.data || res.data || [];
    setStates(list);
    return list;
  };

  const loadDistricts = async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      return [];
    }

    const res = await axios.get(`${BASE_URL}/supervisors/districts`, {
      params: { stateid: stateId },
      headers: commonHeaders,
    });

    const list = res.data?.data || res.data || [];
    setDistricts(list);
    return list;
  };

  const generateNextCode = async (branchId) => {
    if (!branchId) return;
    const res = await axios.get(`${BASE_URL}/supervisors/nextCode`, {
      params: { branchid: branchId },
      headers: commonHeaders,
    });

    const data = res.data?.data || res.data || {};
    const code = data?.Code || data?.code || res.data?.Code || "1";

    setFormData((prev) => ({ ...prev, code }));
  };

  // ✅ fetch full record for edit mode
  const fetchEditDetails = async (supervisorid) => {
    const res = await axios.get(`${BASE_URL}/supervisors/getById`, {
      params: {
        supervisorid,
        branchid: branchIdLS,
      },
      headers: commonHeaders,
    });
    return res.data?.data || res.data || null;
  };

  // ✅ Map DB row to form
  const mapDbToForm = (row) => {
    return {
      supervisorid: row?.fld_supervisorid ?? row?.supervisorid ?? "",
      code: row?.fld_supervisorcode ?? row?.code ?? "",
      name: row?.fld_supervisorname ?? row?.name ?? "",

      branchId: row?.fld_branchid ?? row?.branchid ?? branchIdLS ?? "",

      countryId: row?.fld_countryid ?? row?.countryid ?? "",
      stateId: row?.fld_stateid ?? row?.stateid ?? "",
      districtId: row?.fld_districtid ?? row?.districtid ?? "",

      pinNo: row?.fld_pincode ?? row?.pincode ?? "",
      address1: row?.fld_address1 ?? row?.address1 ?? "",
      address2: row?.fld_address2 ?? row?.address2 ?? "",
      phno: row?.fld_phno ?? row?.phno ?? "",

      accNo: row?.fld_accnumber ?? row?.fld_accno ?? row?.accNo ?? "",
      ifscCode: row?.fld_ifsccode ?? row?.ifscCode ?? "",
      bankName: row?.fld_bankname ?? row?.bankName ?? "",
      branchNameBank:
        row?.fld_branchname ??
        row?.fld_bankbranchname ??
        row?.branchNameBank ??
        "",
    };
  };

  // ✅ Initial load
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        initializingRef.current = true;

        const [branchList, countryList] = await Promise.all([
          loadBranches(),
          loadCountries(),
        ]);

        if (mode === "new") {
          const india = (countryList || []).find(
            (c) => (c.fld_countryname || "").toLowerCase() === "india"
          );

          if (india?.fld_countryid) {
            setFormData((p) => ({ ...p, countryId: india.fld_countryid }));
          }

          if (branchIdLS) {
            setFormData((p) => ({ ...p, branchId: branchIdLS }));
            await generateNextCode(branchIdLS);
          }
        } else {
          const editId =
            passedSupervisor?.fld_supervisorid || passedSupervisor?.supervisorid;

          if (!editId) {
            toast.error("Supervisor id not found from listing selection");
            return;
          }

          const fullRow = await fetchEditDetails(editId);
          const mapped = mapDbToForm(fullRow);
          setFormData((p) => ({ ...p, ...mapped }));

          if (mapped.countryId) await loadStates(mapped.countryId);
          if (mapped.stateId) await loadDistricts(mapped.stateId);
        }
      } catch (e) {
        toast.error("Failed to load supervisor entry data");
      } finally {
        initializingRef.current = false;
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Country change
  useEffect(() => {
    if (initializingRef.current) return;

    (async () => {
      try {
        await loadStates(formData.countryId);
        setFormData((p) => ({ ...p, stateId: "", districtId: "" }));
      } catch {
        toast.error("Failed to load states");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.countryId]);

  // ✅ State change
  useEffect(() => {
    if (initializingRef.current) return;

    (async () => {
      try {
        await loadDistricts(formData.stateId);
        setFormData((p) => ({ ...p, districtId: "" }));
      } catch {
        toast.error("Failed to load districts");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.stateId]);

  // ✅ New mode: branch change -> generate code
  useEffect(() => {
    if (initializingRef.current) return;
    if (mode !== "new") return;
    if (!formData.branchId) return;

    generateNextCode(formData.branchId).catch(() =>
      toast.error("Failed to generate code")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.branchId]);

  const validate = () => {
    const requiredFields = [
      "code",
      "name",
      "branchId",
      "countryId",
      "stateId",
      "districtId",
      "address1",
      "accNo",
      "ifscCode",
      "bankName",
      "branchNameBank",
    ];

    const missing = requiredFields.filter(
      (f) => !String(formData[f] || "").trim()
    );

    if (missing.length) {
      toast.error("Please fill all required fields!");
      return false;
    }

    if (formData.pinNo && !/^\d{6}$/.test(formData.pinNo)) {
      toast.error("Please enter a valid 6-digit PIN code!");
      return false;
    }

    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
      toast.error("Please enter a valid IFSC code!");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      const savePayload = {
        codedet: formData.code,
        name: formData.name,
        branchid: Number(formData.branchId),
        countryid: Number(formData.countryId),
        stateid: Number(formData.stateId),
        districtid: Number(formData.districtId),
        phno: formData.phno || "",
        address1: formData.address1 || "",
        address2: formData.address2 || "",
        accNo: formData.accNo || "",
        ifscCode: formData.ifscCode || "",
        bankName: formData.bankName || "",
        branchNameBank: formData.branchNameBank || "",
        loginuserid: userId,
        ip: "",
        code: "",
      };

      if (mode === "edit") {
        const updatePayload = {
          supervisorid: Number(formData.supervisorid),
          name: formData.name,
          branchid: Number(formData.branchId),
          countryid: Number(formData.countryId),
          stateid: Number(formData.stateId),
          districtid: Number(formData.districtId),
          phno: formData.phno || "",
          address1: formData.address1 || "",
          address2: formData.address2 || "",
          accNo: formData.accNo || "",
          ifscCode: formData.ifscCode || "",
          bankName: formData.bankName || "",
          branchNameBank: formData.branchNameBank || "",
          loginuserid: userId,
          ip: "",
        };

        await axios.post(`${BASE_URL}/supervisors/update`, updatePayload, {
          headers: commonHeaders,
        });

        toast.success("Supervisor updated successfully!");
        navigate(-1);
        return;
      }

      await axios.post(`${BASE_URL}/supervisors/save`, savePayload, {
        headers: commonHeaders,
      });

      toast.success("Supervisor saved successfully!");
      navigate(-1);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SUPERVISOR ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {mode === "edit" ? "Supervisor Update" : "Supervisor Entry"}
            </h1>
          </div>

          {/* Basic Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange("code", e.target.value)}
                disabled={mode === "new"}
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                } ${mode === "new" ? "opacity-80 cursor-not-allowed" : ""}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Branch Name <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.branchId}
                onChange={(e) => handleInputChange("branchId", e.target.value)}
                disabled={mode === "edit"}
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                } ${mode === "edit" ? "opacity-80 cursor-not-allowed" : ""}`}
              >
                <option value="">Select</option>
                {branches.map((b) => (
                  <option key={b.fld_branchid} value={b.fld_branchid}>
                    {b.fld_branchname}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Phone No
              </label>
              <input
                type="text"
                value={formData.phno}
                onChange={(e) => handleInputChange("phno", e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>
          </div>

          {/* Address */}
          <div className={`mb-8 p-4 rounded-lg ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h2 className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-2 rounded`}>
              ADDRESS
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.countryId}
                  onChange={(e) => handleInputChange("countryId", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {countries.map((c) => (
                    <option key={c.fld_countryid} value={c.fld_countryid}>
                      {c.fld_countryname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.stateId}
                  onChange={(e) => handleInputChange("stateId", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {states.map((s) => (
                    <option key={s.fld_stateid} value={s.fld_stateid}>
                      {s.fld_statename}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  District <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.districtId}
                  onChange={(e) => handleInputChange("districtId", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select</option>
                  {districts.map((d) => (
                    <option key={d.fld_districtid} value={d.fld_districtid}>
                      {d.fld_districtname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Pin No
                </label>
                <input
                  type="text"
                  value={formData.pinNo}
                  onChange={(e) => handleInputChange("pinNo", e.target.value)}
                  maxLength={6}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Address 1 <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={formData.address1}
                  onChange={(e) => handleInputChange("address1", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg resize-none ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Address 2
                </label>
                <textarea
                  rows={3}
                  value={formData.address2}
                  onChange={(e) => handleInputChange("address2", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg resize-none ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Bank */}
          <div className={`mb-8 p-4 rounded-lg ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h2 className={`text-lg font-semibold mb-4 ${buttonClass} text-white p-2 rounded`}>
              BANK DETAILS
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Acc No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.accNo}
                  onChange={(e) => handleInputChange("accNo", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  IFSC Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.ifscCode}
                  onChange={(e) => handleInputChange("ifscCode", e.target.value.toUpperCase())}
                  maxLength={11}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Bank Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => handleInputChange("bankName", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Branch Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.branchNameBank}
                  onChange={(e) => handleInputChange("branchNameBank", e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-center items-center gap-6">
            <button
              onClick={handleBack}
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${
                isDark ? "bg-gray-600 hover:bg-gray-700 text-white" : "bg-gray-500 hover:bg-gray-600 text-white"
              } ${loading ? "opacity-70 cursor-not-allowed" : "hover:shadow-xl transform hover:-translate-y-0.5"} transition-all duration-200`}
            >
              <FaArrowLeft /> Back
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${
                loading ? "opacity-70 cursor-not-allowed" : "hover:shadow-xl transform hover:-translate-y-0.5"
              } transition-all duration-200`}
            >
              {mode === "edit" ? "Update" : "Submit"}
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default SupervisorEntry;